/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ChevronLeft,
  Edit,
  Save,
  X,
  Mail,
  Phone,
  MapPin,
  Activity,
  User,
  BookOpen,
  Briefcase,
  Heart,
  CreditCard,
  Key,
} from "lucide-react";
import { AdminContext } from "../../../context/AdminContext";
import ResetPasswordModal from "../../../components/ResetPasswordModal";
import { SUBCITIES } from "../../../utils/constants";

const ClientDetails = () => {
  const { userId } = useParams();
  const {
    profile,
    loading,
    error,
    fetchProfile,
    updateUserDetails,
    resetUserPassword,
  } = useContext(AdminContext);

  const [isEditing, setIsEditing] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [data, setData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    alternativephoneNumber: "",
    address: {
      line1: "",
      line2: "",
    },
    gender: "",
    dob: "",
    status: "",
    image: "",
    language: "",
    subcity: "",
    createdAt: "",
  });

  useEffect(() => {
    if (userId) {
      fetchProfile(userId);
    }
  }, [userId]);

  useEffect(() => {
    if (profile) {
      setData({
        name: profile.name || "",
        email: profile.email || "",
        phoneNumber: profile.phoneNumber || "",
        alternativephoneNumber: profile.alternativePhone || "",
        address: {
          line1: profile.addressLine1 || "",
          line2: profile.addressLine2 || "",
        },
        gender: profile.gender || "Not Selected",
        dob: profile.dob ? profile.dob.split("T")[0] : "Not Selected",
        status: profile.status === "active" ? "Active" : "Inactive",
        image: profile.image || "",
        language: profile.language || "en",
        subcity: profile.subcity || "",
        createdAt: profile.createdAt || "",
        postedJobs: profile.postedJobs || [],
        appliedJobs: profile.appliedJobs || [],
        trainings: profile.trainings || [],
        bookPurchases: profile.bookPurchases || [],
        bookings: profile.bookings || [],
        tourProgress: profile.tourProgress || { completed: false, step: 0 },
      });
    }
  }, [profile]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith("address.")) {
      const field = name.split(".")[1];
      setData((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          [field]: value,
        },
      }));
    } else {
      setData({ ...data, [name]: value });
    }
  };

  const handleSave = async () => {
    const updatedData = {
      name: data.name,
      phoneNumber: data.phoneNumber,
      alternativePhone: data.alternativephoneNumber,
      addressLine1: data.address.line1,
      addressLine2: data.address.line2,
      subcity: data.subcity,
      gender: data.gender,
    };
    if (data.dob && data.dob !== "Not Selected") {
      updatedData.dob = data.dob;
    }

    try {
      const result = await updateUserDetails(userId, updatedData);
      if (!result?.success) return;
      await fetchProfile(userId);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };

  const handleResetPassword = async () => {
    try {
      const success = await resetUserPassword(userId);
      if (success) {
        setShowResetModal(false);
      }
    } catch (error) {
      console.error("Error resetting password:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500 dark:text-dark-muted">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500 dark:text-dark-muted">
          No profile data found
        </div>
      </div>
    );
  }

  // Language mapping
  const languageMap = {
    en: "English",
    am: "Amharic",
    tg: "Tigrinya",
    om: "Afaan Oromoo",
  };

  return (
    <>
      <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-5xl mx-auto pb-12">
        {/* Back & Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <Link
            to="/clients"
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-gray-500 dark:text-dark-muted hover:text-text dark:hover:text-dark-text hover:bg-accent dark:hover:bg-gray-700 transition-all"
          >
            <ChevronLeft size={20} />
            <span className="font-medium">Back to Clients</span>
          </Link>
          {isEditing ? (
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setShowResetModal(true)}
                className="flex items-center gap-2 px-4 sm:px-5 py-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-xl font-semibold transition-colors hover:bg-red-100 dark:hover:bg-red-900/30 hover:border-red-300 dark:hover:border-red-700"
              >
                <Key size={18} /> Reset Password
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="flex items-center gap-2 px-4 sm:px-5 py-2.5 bg-card dark:bg-dark-surface border border-gray-200 dark:border-dark-border text-text dark:text-dark-text rounded-xl font-semibold transition-colors hover:bg-accent dark:hover:bg-gray-700"
              >
                <X size={18} /> Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex items-center gap-2 px-5 sm:px-6 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-semibold transition-all shadow-lg shadow-primary/20 dark:shadow-none"
              >
                <Save size={18} /> Save Changes
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 px-5 sm:px-6 py-2.5 bg-card dark:bg-dark-surface border border-gray-200 dark:border-dark-border text-text dark:text-dark-text hover:border-primary hover:text-primary dark:hover:border-primary dark:hover:text-primary rounded-xl font-semibold transition-all shadow-sm"
            >
              <Edit size={18} /> Edit Profile
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Column: Profile Card */}
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border relative overflow-hidden text-center group">
              {/* Top Half - Gradient Background */}
              <div className="relative bg-gradient-to-r from-primary to-secondary dark:from-gray-950 dark:to-gray-900 p-6 sm:p-8 pb-16">
                <div className="relative mb-6 inline-block">
                  <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full p-1 bg-card dark:bg-dark-surface shadow-lg">
                    {/* Solid border color - No gradient */}
                    <div className="w-full h-full rounded-full p-1 bg-primary">
                      <img
                        src={
                          data.image || "https://picsum.photos/300/300?random=5"
                        }
                        alt="Profile"
                        className="w-full h-full rounded-full object-cover border-4 border-card dark:border-dark-surface"
                      />
                    </div>
                  </div>
                  <div
                    className={`absolute bottom-2 right-2 w-6 h-6 rounded-full border-4 border-card dark:border-dark-surface ${
                      data.status === "Active" ? "bg-emerald-500" : "bg-red-500"
                    }`}
                  ></div>
                </div>
              </div>

              {/* Bottom Half - Blue-Black Background */}
              <div className="bg-slate-900 p-6 sm:p-8 pt-4">
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">
                  {data.name}
                </h2>
                <p className="text-gray-400 font-medium mb-6">{data.email}</p>

                <div className="flex justify-center gap-3 mb-8">
                  <span
                    className={`px-3 sm:px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide ${
                      data.status === "Active"
                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800"
                        : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-200 dark:border-red-800"
                    }`}
                  >
                    {data.status}
                  </span>
                  <span className="px-3 sm:px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide bg-gray-700 text-white">
                    {data.language ? languageMap[data.language] : "English"}
                  </span>
                </div>

                <div className="space-y-4 text-left bg-slate-800/50 p-5 sm:p-6 rounded-2xl">
                  <div className="flex items-center gap-3 text-sm">
                    <Mail size={18} className="text-primary" />
                    <span className="text-white truncate">{data.email}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Phone size={18} className="text-primary" />
                    <span className="text-white">
                      {data.phoneNumber || "Not provided"}
                    </span>
                  </div>
                  {data.alternativephoneNumber && (
                    <div className="flex items-center gap-3 text-sm">
                      <Phone size={18} className="text-primary" />
                      <span className="text-white">
                        {data.alternativephoneNumber}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-900 p-4 sm:p-5 rounded-2xl border border-slate-800 text-center">
                <div className="flex items-center justify-center mx-auto mb-2">
                  <Briefcase size={20} className="text-blue-400" />
                </div>
                <p className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-1">
                  Jobs Posted
                </p>
                <p className="text-lg sm:text-xl font-black text-white">
                  {data.postedJobs?.length || 0}
                </p>
              </div>
              <div className="bg-slate-900 p-4 sm:p-5 rounded-2xl border border-slate-800 text-center">
                <div className="flex items-center justify-center mx-auto mb-2">
                  <BookOpen size={20} className="text-emerald-400" />
                </div>
                <p className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-1">
                  Trainings
                </p>
                <p className="text-lg sm:text-xl font-black text-white">
                  {data.trainings?.length || 0}
                </p>
              </div>
              <div className="bg-slate-900 p-4 sm:p-5 rounded-2xl border border-slate-800 text-center">
                <div className="flex items-center justify-center mx-auto mb-2">
                  <CreditCard size={20} className="text-amber-400" />
                </div>
                <p className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-1">
                  Book Purchases
                </p>
                <p className="text-lg sm:text-xl font-black text-white">
                  {data.bookPurchases?.length || 0}
                </p>
              </div>
              <div className="bg-slate-900 p-4 sm:p-5 rounded-2xl border border-slate-800 text-center">
                <div className="flex items-center justify-center mx-auto mb-2">
                  <Activity size={20} className="text-pink-400" />
                </div>
                <p className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-1">
                  Bookings
                </p>
                <p className="text-lg sm:text-xl font-black text-white">
                  {data.bookings?.length || 0}
                </p>
              </div>
            </div>
          </div>

          {/* Right Column: Details Form */}
          <div className="lg:col-span-8">
            <div className="bg-card dark:bg-dark-surface rounded-3xl p-6 sm:p-8 md:p-10 shadow-sm border border-gray-100 dark:border-dark-border">
              <div className="space-y-10">
                {/* Contact Info */}
                <section>
                  <h3 className="flex items-center gap-2 text-lg font-bold text-text dark:text-dark-text mb-6">
                    <User size={20} className="text-primary" />
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-gray-400 dark:text-dark-muted uppercase tracking-wide">
                        Full Name
                      </label>
                      {isEditing ? (
                        <input
                          name="name"
                          value={data.name}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text font-medium"
                        />
                      ) : (
                        <p className="text-lg font-medium text-text dark:text-dark-text">
                          {data.name}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-gray-400 dark:text-dark-muted uppercase tracking-wide">
                        Email Address
                      </label>
                      {/* Always disabled - cannot be edited */}
                      {isEditing ? (
                        <input
                          name="email"
                          type="email"
                          value={data.email}
                          onChange={handleChange}
                          disabled
                          className="w-full px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none text-text dark:text-dark-text font-medium opacity-70 cursor-not-allowed"
                        />
                      ) : (
                        <p className="text-lg font-medium text-text dark:text-dark-text">
                          {data.email}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-gray-400 dark:text-dark-muted uppercase tracking-wide">
                        Phone Number
                      </label>
                      {isEditing ? (
                        <input
                          name="phoneNumber"
                          value={data.phoneNumber}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text font-medium"
                        />
                      ) : (
                        <p className="text-lg font-medium text-text dark:text-dark-text">
                          {data.phoneNumber || "Not provided"}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-gray-400 dark:text-dark-muted uppercase tracking-wide">
                        Alternative Phone
                      </label>
                      {isEditing ? (
                        <input
                          name="alternativephoneNumber"
                          value={data.alternativephoneNumber}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text font-medium"
                        />
                      ) : (
                        <p className="text-lg font-medium text-text dark:text-dark-text">
                          {data.alternativephoneNumber || "Not provided"}
                        </p>
                      )}
                    </div>
                  </div>
                </section>

                <div className="w-full h-px bg-gray-100 dark:bg-gray-700"></div>

                {/* Address */}
                <section>
                  <h3 className="flex items-center gap-2 text-lg font-bold text-text dark:text-dark-text mb-6">
                    <MapPin
                      size={20}
                      className="text-blue-600 dark:text-blue-400"
                    />
                    Address Details
                  </h3>
                  <div className="grid grid-cols-1 gap-6">
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-gray-400 dark:text-dark-muted uppercase tracking-wide">
                        Street Address
                      </label>
                      {isEditing ? (
                        <input
                          name="address.line1"
                          value={data.address.line1}
                          onChange={handleChange}
                          placeholder="Street Address"
                          className="w-full px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text font-medium"
                        />
                      ) : (
                        <p className="text-lg font-medium text-text dark:text-dark-text">
                          {data.address.line1 || "Not provided"}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-gray-400 dark:text-dark-muted uppercase tracking-wide">
                        Additional Address Info
                      </label>
                      {isEditing ? (
                        <input
                          name="address.line2"
                          value={data.address.line2}
                          onChange={handleChange}
                          placeholder="Apt / Suite / Area"
                          className="w-full px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text font-medium"
                        />
                      ) : (
                        <p className="text-lg font-medium text-text dark:text-dark-text">
                          {data.address.line2 || "Not provided"}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-gray-400 dark:text-dark-muted uppercase tracking-wide">
                        Subcity
                      </label>
                      {isEditing ? (
                        <select
                          name="subcity"
                          value={data.subcity}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text font-medium"
                        >
                          <option value="">Select Subcity</option>
                          {SUBCITIES.map((subcity) => (
                            <option key={subcity} value={subcity}>
                              {subcity}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <p className="text-lg font-medium text-text dark:text-dark-text">
                          {data.subcity || "Not provided"}
                        </p>
                      )}
                    </div>
                  </div>
                </section>

                <div className="w-full h-px bg-gray-100 dark:bg-gray-700"></div>

                {/* Personal */}
                <section>
                  <h3 className="flex items-center gap-2 text-lg font-bold text-text dark:text-dark-text mb-6">
                    <Activity
                      size={20}
                      className="text-emerald-600 dark:text-emerald-400"
                    />
                    Personal Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-gray-400 dark:text-dark-muted uppercase tracking-wide">
                        Gender
                      </label>
                      {isEditing ? (
                        <select
                          name="gender"
                          value={data.gender}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text font-medium"
                        >
                          <option value="Not Selected">Select Gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                        </select>
                      ) : (
                        <p className="text-lg font-medium text-text dark:text-dark-text">
                          {data.gender}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-gray-400 dark:text-dark-muted uppercase tracking-wide">
                        Date of Birth
                      </label>
                      {isEditing ? (
                        <input
                          type="date"
                          name="dob"
                          value={data.dob}
                          max={
                            new Date(
                              new Date().setFullYear(
                                new Date().getFullYear() - 18,
                              ),
                            )
                              .toISOString()
                              .split("T")[0]
                          }
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text font-medium"
                        />
                      ) : (
                        <p className="text-lg font-medium text-text dark:text-dark-text">
                          {data.dob}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-gray-400 dark:text-dark-muted uppercase tracking-wide">
                        Preferred Language
                      </label>
                      {/* Always disabled - cannot be edited */}
                      {isEditing ? (
                        <select
                          name="language"
                          value={data.language}
                          onChange={handleChange}
                          disabled
                          className="w-full px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none text-text dark:text-dark-text font-medium opacity-70 cursor-not-allowed"
                        >
                          <option value="en">English</option>
                          <option value="am">Amharic</option>
                          <option value="tg">Tigrinya</option>
                          <option value="om">Afaan Oromoo</option>
                        </select>
                      ) : (
                        <p className="text-lg font-medium text-text dark:text-dark-text">
                          {languageMap[data.language] || "English"}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="mt-6">
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-gray-400 dark:text-dark-muted uppercase tracking-wide">
                        Joined Date
                      </label>
                      <p className="text-lg font-medium text-text dark:text-dark-text">
                        {data.createdAt
                          ? new Date(data.createdAt).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              },
                            )
                          : "N/A"}
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reset Password Modal */}
      <ResetPasswordModal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        onConfirm={handleResetPassword}
        userData={{
          name: data.name,
          email: data.email,
          phoneNumber: data.phoneNumber,
          image: data.image,
        }}
      />
    </>
  );
};

export default ClientDetails;
